/**
 * useMapLayers Hook
 * Creates deck.gl layers for the map matching the original AsseTrac design.
 *
 * Layer stack (bottom to top):
 * 1. BoundaryLayer   - PathLayer white project boundary lines
 * 2. AreaOverlayLayer - PolygonLayer for compliance zone polygons
 * 3. AssetClusterLayer - IconLayer + TextLayer for clustered mat markers
 * 4. AssetIconLayer   - IconLayer for individual assets (non-mat + unclustered mats)
 *
 * Asset Shape Language:
 * - CIRCLE shape → Battery-powered assets (gateways) - deviceType === 'gateway'
 * - SQUIRCLE shape → Beacon-enabled assets (mat, tool, material, waste, trailer, etc.)
 *
 * Note: When icons are loaded, the IconLayer uses sprite atlas images which render
 * with their native shapes. The fallback ScatterplotLayer renders circles for all
 * assets (deck.gl limitation - ScatterplotLayer only supports circles).
 * See src/utils/assetShapeLanguage.js for the shape language implementation.
 */

import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { IconLayer, PathLayer, PolygonLayer, TextLayer, ScatterplotLayer } from '@deck.gl/layers';
import Supercluster from 'supercluster';

import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setTooltip, clearTooltip, updateViewport } from '../store/slices/mapSlice';
import { MAP_LAYERS } from '../theme';
import { isCircleShape } from '../utils/assetShapeLanguage';
import { AREA_ELEMENT_LINE } from '../utils/areaElementLayer';

// ---------- CONSTANTS ----------

const ICON_ATLAS = '/icons/icons.png';

// Area overlay colors from the original projectAreasConfig
const AREA_COLORS = {
  'Noxious_Weeds.png':    { fill: [231, 59, 32, 75],  border: [231, 59, 32] },
  'Move_Rule.png':        { fill: [231, 59, 32, 75],  border: [231, 59, 32] },
  'Organic_Farm.png':     { fill: [231, 59, 32, 75],  border: [231, 59, 32] },
  'Do_Not_Disturb.png':   { fill: [231, 59, 32, 75],  border: [231, 59, 32] },
  'Do_Not_Enter.png':     { fill: [231, 59, 32, 75],  border: [231, 59, 32] },
  'Wetland.png':          { fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'Agricultural_Land.png':{ fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'Project_Yard.png':     { fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'Cleaning_Station.png': { fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'End_Of_Life.png':      { fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'Resale.png':           { fill: [42, 110, 192, 40], border: [42, 110, 192] },
  'Right_Of_Way.png':     { fill: [255, 255, 255, 20], border: [255, 255, 255] },
  'Access_Road.png':      { fill: [0, 0, 0, 40],      border: [100, 100, 100] },
  'Structure.png':        { fill: [0, 0, 0, 60],      border: [50, 50, 50] },
};

// Icon name used for mat clusters (the same icon shown at smaller size)
const MAT_CLUSTER_ICON = 'Mat_Marker.png';

// SuperCluster config (matches original)
const CLUSTER_RADIUS = 40;
const CLUSTER_MAX_ZOOM = 11;

// Base icon size calculation matching original
const getIconSize = (zoom) => {
  const base = 45;
  const size = base + (zoom - 10) * 8;
  return Math.max(size, 45);
};

// ---------- HOOK ----------

const useMapLayers = ({
  assetsData = {},
  projectBoundariesData = {},
  overlaysData = {},
  movesData = {},
  visibleOverlays = [],
  showProjectMarkers = true,
  showBoundaryFill = true,
  // AST-96: a manual project's mapped KMZ elements, already shaped by
  // utils/areaElementLayer.areaElementMarkers. Empty for every other map.
  areaElementMarkers = [],
  // AST-96: the same elements' stored shapes, from areaElementShapes:
  // { polygons: [...], paths: [...] }. Drawn under the markers.
  areaElementShapes = null,
  onAssetClick,
  onProjectClick,
  onProjectHover,
}) => {
  const dispatch = useAppDispatch();
  const { iconData, iconsLoaded } = useAppSelector((state) => state.ui);
  const { viewport } = useAppSelector((state) => state.map);

  // Track zoom for clustering
  const [zoomLevel, setZoomLevel] = useState(viewport.zoom || 11);
  const zoomTimerRef = useRef(null);

  // Debounced zoom update
  useEffect(() => {
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      setZoomLevel(viewport.zoom || 11);
    }, 200);
    return () => clearTimeout(zoomTimerRef.current);
  }, [viewport.zoom]);

  // --- Data transforms ---

  const assetsArray = useMemo(
    () => Object.values(assetsData).filter((a) => a.coordinates),
    [assetsData]
  );

  // Separate mats, beacons (non-mat), and gateways
  // Note: Gateway assets (battery-powered) use CIRCLE shape; all others (beacon-enabled) use SQUIRCLE shape
  // See src/utils/assetShapeLanguage.js for shape language details
  const { matAssets, nonMatAssets, gatewayAssets } = useMemo(() => {
    const mats = [];
    const gateways = [];
    const others = [];
    for (const a of assetsArray) {
      const typeName = a.assetTypeName || a.type?.name || '';
      // Use the shape language to determine if this is a gateway (circle shape) asset
      if (typeName === 'Mat') {
        mats.push(a);
      } else if (isCircleShape(a)) {
        // Battery-powered assets with gateways get CIRCLE shape
        gateways.push(a);
      } else {
        // Beacon-enabled assets get SQUIRCLE shape
        others.push(a);
      }
    }
    return { matAssets: mats, nonMatAssets: others, gatewayAssets: gateways };
  }, [assetsArray]);

  // Projects to polygon arrays
  const projectsArray = useMemo(() => {
    return Object.values(projectBoundariesData)
      .filter((p) => p.boundaries)
      .map((project) => {
        let path = [];
        try {
          if (typeof project.boundaries === 'string') {
            const geojson = JSON.parse(project.boundaries);
            if (geojson.type === 'Polygon' && geojson.coordinates) {
              path = geojson.coordinates[0];
            }
          } else if (Array.isArray(project.boundaries)) {
            path = project.boundaries.map((b) => [b.longitude, b.latitude]);
          }
        } catch {
          // ignore
        }
        return { ...project, path };
      })
      .filter((p) => p.path.length > 0);
  }, [projectBoundariesData]);

  // ASS-81 Part A: point-only projects (manual projects with a location but
  // no parseable boundary shape). DashboardPage.jsx guarantees a project
  // lands in exactly one of projectsArray / pointProjectsArray, never both --
  // projectsArray requires a `boundaries` key; these entries carry
  // `location` instead, with no `boundaries` key at all. No polygon or path
  // layer is ever built from this array -- only the hexagon marker/label
  // layers, further down, read it.
  const pointProjectsArray = useMemo(() => {
    return Object.values(projectBoundariesData)
      .filter((p) => !p.boundaries && p.location
        && typeof p.location.lat === 'number' && typeof p.location.lng === 'number'
        && !Number.isNaN(p.location.lat) && !Number.isNaN(p.location.lng))
      .map((project) => ({
        ...project,
        position: [project.location.lng, project.location.lat],
      }));
  }, [projectBoundariesData]);

  // --- SuperCluster for mats ---

  const supercluster = useMemo(() => {
    const index = new Supercluster({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
    });

    const points = matAssets.map((a) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [a.coordinates.longitude, a.coordinates.latitude],
      },
      properties: a,
    }));

    index.load(points);
    return index;
  }, [matAssets]);

  const { clusters, unclusteredMats } = useMemo(() => {
    const z = Math.floor(zoomLevel);
    const raw = supercluster.getClusters([-180, -85, 180, 85], z);
    const clusterArr = [];
    const singles = [];
    for (const f of raw) {
      if (f.properties.cluster) {
        clusterArr.push({
          coordinates: f.geometry.coordinates,
          count: f.properties.point_count,
          countAbbreviated: f.properties.point_count_abbreviated,
          clusterId: f.properties.cluster_id,
        });
      } else {
        singles.push(f.properties);
      }
    }
    return { clusters: clusterArr, unclusteredMats: singles };
  }, [supercluster, zoomLevel]);

  // Combine non-mat + unclustered mats for the individual icon layer
  const individualAssets = useMemo(
    () => [...nonMatAssets, ...unclusteredMats],
    [nonMatAssets, unclusteredMats]
  );

  // Resolve icon name for an asset; strip file extensions
  const getIconName = useCallback(
    (asset) => {
      const iconName = asset.type?.iconName || 'Mat_Marker';
      // The atlas keys include ".png"; asset data may or may not
      const withExt = iconName.endsWith('.png') ? iconName : `${iconName}.png`;
      if (iconsLoaded && iconData[withExt]) return withExt;
      // Fallback without .png
      if (iconsLoaded && iconData[iconName]) return iconName;
      return 'Mat_Marker.png';
    },
    [iconData, iconsLoaded]
  );

  // --- Tooltip helpers ---

  const handleAssetHover = useCallback(
    (info) => {
      if (info.object) {
        dispatch(
          setTooltip({
            x: info.x,
            y: info.y,
            type: 'asset',
            unitNumber: info.object.unitNumber,
            typeName: info.object.assetTypeName || info.object.type?.name,
            subTypeName: info.object.subType?.name,
            lastReceived: info.object.lastReceived,
            cleanStatus: info.object.cleanStatus,
          })
        );
      } else {
        dispatch(clearTooltip());
      }
    },
    [dispatch]
  );

  const handleClusterClick = useCallback(
    (info) => {
      if (info.object?.clusterId != null) {
        const expansionZoom = Math.min(
          supercluster.getClusterExpansionZoom(info.object.clusterId),
          20
        );
        // The map component will pick up the viewport change
        dispatch(clearTooltip());
        dispatch(
          updateViewport({
            longitude: info.object.coordinates[0],
            latitude: info.object.coordinates[1],
            zoom: expansionZoom,
          })
        );
      }
    },
    [supercluster, dispatch]
  );

  // --- LAYER CONSTRUCTION ---

  // 1. Boundary Layers (PathLayer + PolygonLayer with fill + Hexagon markers)
  const boundaryLayers = useMemo(() => {
    // ASS-81 Part A: don't early-return when there are no boundary-shaped
    // projects -- point-only projects still need their hexagon markers even
    // if every boundary project has been filtered out (or there are none).
    if (projectsArray.length === 0 && pointProjectsArray.length === 0) return [];
    
    const layers = [];
    
    // Filled polygon layer with 20% opacity white (only if showBoundaryFill is true)
    if (showBoundaryFill && projectsArray.length > 0) {
      layers.push(
        new PolygonLayer({
          id: 'project-boundaries-fill',
          data: projectsArray,
          pickable: false,
          stroked: false,
          filled: true,
          getPolygon: (d) => d.path,
          getFillColor: MAP_LAYERS.projectBoundary.fillColor,
        })
      );
    }
    
    // White boundary outline
    if (projectsArray.length > 0) {
      layers.push(
        new PathLayer({
          id: 'project-boundaries-outline',
          data: projectsArray,
          pickable: false,
          getPath: (d) => d.path,
          getColor: MAP_LAYERS.projectBoundary.strokeColor,
          getWidth: 0.75,
          widthScale: 20,
          widthMinPixels: MAP_LAYERS.projectBoundary.strokeWidth,
          widthMaxPixels: MAP_LAYERS.projectBoundary.strokeWidth,
        })
      );
    }
    
    // Hexagon markers with P/Y labels (only if showProjectMarkers is true)
    if (showProjectMarkers) {
      // Calculate center point for each boundary project
      const boundaryMarkers = projectsArray.map((project) => {
        const path = project.path;
        if (!path || path.length === 0) return null;
        
        // Calculate centroid
        let totalLongitude = 0, totalLatitude = 0;
        for (const [lng, lat] of path) {
          totalLongitude += lng;
          totalLatitude += lat;
        }
        const centerLng = totalLongitude / path.length;
        const centerLat = totalLatitude / path.length;
        
        return {
          ...project,
          position: [centerLng, centerLat],
          isYard: project.type === 'yard',
        };
      }).filter(Boolean);

      // ASS-81 Part A: point-only projects (manual, no boundary shape)
      // already carry `position` from pointProjectsArray -- they skip the
      // polygon/path layers above entirely but join the SAME hexagon marker
      // and label layers here, so they render identically to boundary
      // markers (same shape, same click handler) at their pinned lat/lng.
      const projectMarkers = [...boundaryMarkers, ...pointProjectsArray];
      
      // Shadow layer for hexagon (positioned slightly below) - render first
      layers.push(
        new ScatterplotLayer({
          id: 'project-hexagon-shadow',
          data: projectMarkers,
          pickable: false,
          filled: true,
          stroked: false,
          radiusUnits: 'pixels',
          getPosition: (d) => {
            // Offset shadow slightly down and right
            const [lng, lat] = d.position;
            const [shadowXOffset, shadowYOffset] = MAP_LAYERS.hexagonMarker.shadowOffset;
            return [lng + shadowXOffset, lat + shadowYOffset];
          },
          getRadius: MAP_LAYERS.hexagonMarker.radius,
          getFillColor: MAP_LAYERS.hexagonMarker.shadowColor,
        })
      );
      
      // Hexagon marker layer (orange background with white border)
      layers.push(
        new ScatterplotLayer({
          id: 'project-hexagon-markers',
          data: projectMarkers,
          pickable: true,
          filled: true,
          stroked: true,
          radiusUnits: 'pixels',
          lineWidthUnits: 'pixels',
          getPosition: (d) => d.position,
          getRadius: MAP_LAYERS.hexagonMarker.radius,
          getFillColor: MAP_LAYERS.hexagonMarker.fillColor,
          getLineColor: MAP_LAYERS.hexagonMarker.strokeColor,
          getLineWidth: MAP_LAYERS.hexagonMarker.strokeWidth,
          onClick: onProjectClick,
          onHover: onProjectHover,
        })
      );
      
      // Text layer for P/Y labels
      layers.push(
        new TextLayer({
          id: 'project-hexagon-labels',
          data: projectMarkers,
          pickable: false,
          getPosition: (d) => d.position,
          getText: (d) => d.isYard ? 'Y' : 'P',
          getSize: MAP_LAYERS.hexagonMarker.textSize,
          getColor: MAP_LAYERS.hexagonMarker.textColor,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontWeight: 700,
          sizeUnits: 'pixels',
        })
      );
    }
    
    return layers;
  }, [projectsArray, pointProjectsArray, showProjectMarkers, showBoundaryFill, onProjectClick, onProjectHover]);

  // 2. Area overlay PolygonLayer (filtered by visibleOverlays)
  const overlayLayers = useMemo(() => {
    const overlayEntries = Object.values(overlaysData).filter((o) => {
      if (visibleOverlays.length === 0) return false;
      return visibleOverlays.includes(o.areaTypeId);
    });
    if (overlayEntries.length === 0) return [];

    return [
      new PolygonLayer({
        id: 'area-overlays',
        data: overlayEntries,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthMinPixels: 2,
        getPolygon: (d) => {
          try {
            if (typeof d.geoJson === 'string') {
              const geo = JSON.parse(d.geoJson);
              return geo.coordinates?.[0] || [];
            }
            if (d.geoJson?.coordinates) return d.geoJson.coordinates[0] || [];
          } catch {
            // ignore
          }
          return [];
        },
        getFillColor: (d) => {
          const icon = d.icon || '';
          return AREA_COLORS[icon]?.fill || [42, 110, 192, 40];
        },
        getLineColor: (d) => {
          const icon = d.icon || '';
          return AREA_COLORS[icon]?.border || [42, 110, 192];
        },
        getLineWidth: 2,
        onHover: (info) => {
          if (info.object) {
            dispatch(
              setTooltip({
                x: info.x,
                y: info.y,
                type: 'area',
                name: info.object.name,
                areaType: info.object.areaTypeName,
              })
            );
          } else {
            dispatch(clearTooltip());
          }
        },
      }),
    ];
  }, [overlaysData, visibleOverlays, dispatch]);

  // Asset type base colors for ScatterplotLayer fallback
  const ASSET_TYPE_COLORS = {
    'Mat': [139, 90, 43],
    'Heavy Equipment': [255, 152, 0],
    'Light Equipment': [255, 193, 7],
    'Large Truck': [25, 118, 210],
    'Medium Truck': [30, 136, 229],
    'Small Truck': [33, 150, 243],
    'Trailer': [156, 39, 176],
    'Containers': [121, 85, 72],
    'Materials': [158, 158, 158],
    'Small Tools': [76, 175, 80],
    'Waste': [244, 67, 54],
    'default': [236, 79, 54],
  };

  // 3. Cluster layers (IconLayer when icons loaded, ScatterplotLayer fallback)
  const clusterLayers = useMemo(() => {
    if (clusters.length === 0) return [];

    if (iconsLoaded && Object.keys(iconData).length > 0) {
      return [
        new IconLayer({
          id: 'mat-clusters-icon',
          data: clusters,
          pickable: true,
          iconAtlas: ICON_ATLAS,
          iconMapping: iconData,
          getIcon: () => MAT_CLUSTER_ICON,
          getPosition: (d) => d.coordinates,
          getSize: (d) => (d.count > 999 ? 24 : 17),
          sizeScale: 1,
          sizeUnits: 'pixels',
          onClick: handleClusterClick,
        }),
        new TextLayer({
          id: 'mat-clusters-text',
          data: clusters,
          pickable: false,
          getPosition: (d) => d.coordinates,
          getText: (d) => String(d.countAbbreviated),
          getSize: (d) => (d.count > 999 ? 9 : 10),
          getColor: [0, 0, 0, 255],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontWeight: 700,
          sizeUnits: 'pixels',
        }),
      ];
    }

    // Fallback: ScatterplotLayer for clusters
    return [
      new ScatterplotLayer({
        id: 'mat-clusters-dot',
        data: clusters,
        pickable: true,
        getPosition: (d) => d.coordinates,
        getRadius: (d) => Math.min(20, 8 + Math.log2(d.count) * 3),
        getFillColor: [139, 90, 43, 200],
        getLineColor: [255, 255, 255, 200],
        stroked: true,
        lineWidthMinPixels: 1,
        radiusUnits: 'pixels',
        onClick: handleClusterClick,
      }),
      new TextLayer({
        id: 'mat-clusters-text',
        data: clusters,
        pickable: false,
        getPosition: (d) => d.coordinates,
        getText: (d) => String(d.countAbbreviated),
        getSize: 10,
        getColor: [255, 255, 255, 255],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontWeight: 700,
        sizeUnits: 'pixels',
      }),
    ];
  }, [clusters, iconsLoaded, iconData, handleClusterClick]);

  // 4. Individual asset layers (IconLayer when icons loaded, ScatterplotLayer fallback)
  const assetLayers = useMemo(() => {
    if (individualAssets.length === 0) return [];

    if (iconsLoaded && Object.keys(iconData).length > 0) {
      const iconSize = getIconSize(zoomLevel);
      return [
        new IconLayer({
          id: 'assets-icon',
          data: individualAssets,
          pickable: true,
          iconAtlas: ICON_ATLAS,
          iconMapping: iconData,
          getIcon: (d) => getIconName(d),
          getPosition: (d) => [d.coordinates.longitude, d.coordinates.latitude],
          getSize: (d) => {
            const typeName = d.assetTypeName || d.type?.name || '';
            if (typeName === 'Mat') return iconSize * 1.5;
            return iconSize * 2.2;
          },
          sizeScale: 1 / 4,
          sizeUnits: 'pixels',
          opacity: 1,
          onClick: onAssetClick,
          onHover: handleAssetHover,
          updateTriggers: {
            getIcon: [iconsLoaded],
            getSize: [zoomLevel],
          },
        }),
      ];
    }

    // Fallback: ScatterplotLayer
    // Note: ScatterplotLayer only supports circles. When icons are loaded,
    // the IconLayer renders assets with their appropriate shapes from the sprite atlas.
    // See src/utils/assetShapeLanguage.js for shape language (CIRCLE for gateway, SQUIRCLE for beacon-enabled)
    return [
      new ScatterplotLayer({
        id: 'assets-dots',
        data: individualAssets,
        pickable: true,
        opacity: 0.9,
        stroked: true,
        filled: true,
        radiusMinPixels: 4,
        radiusMaxPixels: 16,
        lineWidthMinPixels: 1,
        getPosition: (d) => [d.coordinates.longitude, d.coordinates.latitude],
        getRadius: 6,
        radiusUnits: 'pixels',
        getFillColor: (d) => {
          const typeName = d.assetTypeName || d.type?.name || 'default';
          return ASSET_TYPE_COLORS[typeName] || ASSET_TYPE_COLORS.default;
        },
        getLineColor: [40, 40, 40, 200],
        onClick: onAssetClick,
        onHover: handleAssetHover,
      }),
    ];
  }, [
    individualAssets,
    iconsLoaded,
    iconData,
    zoomLevel,
    getIconName,
    onAssetClick,
    handleAssetHover,
  ]);

  // 5. Gateway layer (always on top of other assets)
  // Gateway assets are battery-powered and use CIRCLE shape per the asset shape language.
  // The ScatterplotLayer fallback naturally renders circles, which is correct for gateways.
  const gatewayLayers = useMemo(() => {
    if (gatewayAssets.length === 0) return [];
    if (iconsLoaded && Object.keys(iconData).length > 0) {
      const iconSize = getIconSize(zoomLevel);
      return [
        new IconLayer({
          id: 'gateways-icon',
          data: gatewayAssets,
          pickable: true,
          iconAtlas: ICON_ATLAS,
          iconMapping: iconData,
          getIcon: (d) => getIconName(d),
          getPosition: (d) => [d.coordinates.longitude, d.coordinates.latitude],
          getSize: iconSize * 2.2,
          sizeScale: 1 / 4,
          sizeUnits: 'pixels',
          opacity: 1,
          onClick: onAssetClick,
          onHover: handleAssetHover,
          updateTriggers: { getIcon: [iconsLoaded], getSize: [zoomLevel] },
        }),
      ];
    }
    return [
      new ScatterplotLayer({
        id: 'gateways-dots',
        data: gatewayAssets,
        pickable: true,
        opacity: 0.9,
        stroked: true,
        filled: true,
        radiusMinPixels: 6,
        radiusMaxPixels: 18,
        lineWidthMinPixels: 2,
        getPosition: (d) => [d.coordinates.longitude, d.coordinates.latitude],
        getRadius: 8,
        getFillColor: [236, 79, 54, 230],
        getLineColor: [255, 255, 255, 200],
        radiusUnits: 'pixels',
        onClick: onAssetClick,
        onHover: handleAssetHover,
      }),
    ];
  }, [gatewayAssets, iconsLoaded, iconData, zoomLevel, getIconName, onAssetClick, handleAssetHover]);

  // 6. Waypoint/Moves layers (for asset detail timeline)
  const waypointLayers = useMemo(() => {
    const movesArray = Object.values(movesData);
    if (movesArray.length === 0) return [];

    return [
      new ScatterplotLayer({
        id: 'waypoints',
        data: movesArray,
        pickable: true,
        getPosition: (d) => [d.coordinates.longitude, d.coordinates.latitude],
        getRadius: (d) => {
          if (d.type === 'start') return 8;
          if (d.type === 'end') return 10;
          return 5;
        },
        radiusUnits: 'pixels',
        getFillColor: (d) => {
          if (d.type === 'start') return [76, 175, 80, 200]; // green
          if (d.type === 'end') return [236, 79, 54, 255]; // orange
          return [255, 255, 255, 150]; // white path dots
        },
        getLineColor: [40, 40, 40, 200],
        stroked: true,
        lineWidthMinPixels: 1,
      }),
    ];
  }, [movesData]);

  // --- AST-96: mapped KMZ elements (Structures / Yards / Other) ---
  // Hover reuses the existing 'area' tooltip (name + type line).
  const handleAreaElementHover = useCallback(
    (info) => {
      if (info.object) {
        dispatch(setTooltip({
          type: 'area',
          name: info.object.name,
          areaType: info.object.sectionLabel,
          x: info.x,
          y: info.y,
        }));
      } else {
        dispatch(clearTooltip());
      }
    },
    [dispatch]
  );

  const areaElementLayers = useMemo(() => {
    const polys = areaElementShapes?.polygons || [];
    const paths = areaElementShapes?.paths || [];
    if (!areaElementMarkers.length && !polys.length && !paths.length) return [];
    return [
      // Yards and other polygons: translucent fill so the basemap still reads
      // through, solid outline in the section colour.
      new PolygonLayer({
        id: 'area-element-polygons',
        data: polys,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthUnits: 'pixels',
        lineWidthMinPixels: 2,
        getPolygon: (d) => d.polygon,
        getFillColor: (d) => [...d.fill, 70],
        getLineColor: (d) => [...d.line, 255],
        getLineWidth: 2,
        onHover: handleAreaElementHover,
        updateTriggers: { getFillColor: polys, getLineColor: polys },
      }),
      // Roads and other lines: a white casing under a coloured line, so they
      // stay visible on dark and light parts of the satellite image.
      new PathLayer({
        id: 'area-element-paths-casing',
        data: paths,
        pickable: false,
        widthUnits: 'pixels',
        getPath: (d) => d.path,
        getColor: [255, 255, 255, 200],
        getWidth: 5,
      }),
      new PathLayer({
        id: 'area-element-paths',
        data: paths,
        pickable: true,
        widthUnits: 'pixels',
        getPath: (d) => d.path,
        getColor: (d) => [...d.line, 255],
        getWidth: 3,
        onHover: handleAreaElementHover,
        updateTriggers: { getColor: paths },
      }),
      new ScatterplotLayer({
        id: 'area-elements',
        data: areaElementMarkers,
        pickable: true,
        stroked: true,
        filled: true,
        radiusUnits: 'pixels',
        lineWidthUnits: 'pixels',
        getPosition: (d) => d.position,
        getRadius: (d) => d.radius,
        getFillColor: (d) => [...d.fill, 235],
        // AST-100 (Mark): orange line, not white.
        getLineColor: [...AREA_ELEMENT_LINE, 255],
        getLineWidth: 2,
        onHover: handleAreaElementHover,
        updateTriggers: { getFillColor: areaElementMarkers, getRadius: areaElementMarkers },
      }),
    ];
  }, [areaElementMarkers, areaElementShapes, handleAreaElementHover]);

  // --- Combine all layers ---
  // Area elements sit above boundaries/overlays and below assets, so beacon
  // assets (on Both projects) are never hidden under them.
  const allLayers = useMemo(
    () => [...boundaryLayers, ...overlayLayers, ...areaElementLayers, ...waypointLayers, ...clusterLayers, ...assetLayers, ...gatewayLayers],
    [boundaryLayers, overlayLayers, areaElementLayers, waypointLayers, clusterLayers, assetLayers, gatewayLayers]
  );

  return {
    assetLayers,
    boundaryLayers,
    overlayLayers,
    clusterLayers,
    gatewayLayers,
    waypointLayers,
    areaElementLayers,
    allLayers,
  };
};

export default useMapLayers;
